/**
 * Previous / Close & next — the linear walk, at the END of the section form,
 * in flow (D-028).
 *
 * Brief §2: *"The map is for jumping; the walk is for finishing."* A surveyor
 * who never opens the map still finishes the job by tapping Close & next
 * through every section in the engine's order; these two buttons are that
 * walk. They used to ride in a sticky bottom bar (`SectionNav`) alongside the
 * way back to the hall indicator; the map bar is the way up and out now, on
 * every screen, so the walk buttons sit where the form ends — the reader's
 * natural next thing after the last question.
 *
 * Rules kept exactly:
 *   - the primary button names its DESTINATION on a second line, so a
 *     surveyor sees "Close & next / Pit · HW 2" before they tap;
 *   - closing is a link, never a submit — nothing gates it (§4 rule 8);
 *   - the end of the walk is "Close & submit" only when the wizard says the
 *     survey is finishable, otherwise the next unwalked thing with the face
 *     "Keep going" (progressive disclosure, D-014/D-017);
 *   - the aria-labels are stable — `Close & next: {title}`,
 *     `Previous section: {title}` — because tests/e2e/section-title.ts keys
 *     every walking spec's settle signal on them.
 *
 * Anchors, so long-press, open-in-new-tab, and a screen reader's link list
 * all work on the app's navigation.
 */

import { useState, type ReactNode } from 'react';

/** Where a button goes, and what the surveyor will be looking at when it does. */
export interface SectionStop {
  title: string;
  href: string;
}

export interface WalkButtonsProps {
  /** Omitted on the first section in the walk order. */
  previous?: SectionStop;
  /** Always present: the last section's `next` is the submit screen. */
  next: SectionStop;
  /** True when `next` is close/submit rather than another section. */
  submit: boolean;
  /**
   * Overrides the "Close & next" face at the end of the walk when submit is
   * not yet disclosed — "Keep going" toward the next unwalked thing.
   */
  nextFace?: string;
  /**
   * D-030: one line naming what is still open on a STRUCTURE section — e.g.
   * "Car 2 isn't in a group yet." When set, the first tap on Close & next
   * surfaces the line with Keep placing / Continue anyway instead of
   * navigating. Continue is an ungated link and open items travel as gaps —
   * §4 rule 8's lifecycle holds; only the moment of the one sentence moved
   * (completeness talks exactly once, at Close & Next).
   */
  nextGuard?: string;
}

export function WalkButtons({
  previous,
  next,
  submit,
  nextFace,
  nextGuard,
}: WalkButtonsProps): ReactNode {
  const face = submit ? 'Close & submit' : (nextFace ?? 'Close & next');
  const [asking, setAsking] = useState(false);

  if (nextGuard && asking) {
    return (
      <nav className="walk walk--guard" aria-label="Move between sections">
        <p className="ruleline" data-legibility="helper" data-testid="walk-guard">
          {nextGuard}
        </p>
        <div className="rowbtns">
          <button type="button" className="btn btn--ghost" onClick={() => setAsking(false)}>
            Keep placing
          </button>
          <a className="btn btn--primary" href={next.href} aria-label={`Continue anyway: ${next.title}`}>
            Continue anyway
          </a>
        </div>
      </nav>
    );
  }

  return (
    <nav className="walk" aria-label="Move between sections">
      {previous ? (
        <a
          className="btn btn--ghost btn--stack"
          href={previous.href}
          aria-label={`Previous section: ${previous.title}`}
        >
          <span className="btn__face">
            <span className="btn__glyph" aria-hidden="true">
              ◂
            </span>
            Previous
          </span>
          <span className="btn__dest">{previous.title}</span>
        </a>
      ) : null}

      {nextGuard ? (
        <button
          type="button"
          className="btn btn--primary btn--stack"
          aria-label={`${face}: ${next.title}`}
          onClick={() => setAsking(true)}
        >
          <span className="btn__face">
            {face}
            <span className="btn__glyph" aria-hidden="true">
              ▸
            </span>
          </span>
          <span className="btn__dest">{next.title}</span>
        </button>
      ) : (
        <a
          className={submit ? 'btn btn--submit btn--stack' : 'btn btn--primary btn--stack'}
          href={next.href}
          aria-label={submit ? 'Close & submit' : `${face}: ${next.title}`}
        >
          <span className="btn__face">
            {face}
            <span className="btn__glyph" aria-hidden="true">
              ▸
            </span>
          </span>
          <span className="btn__dest">{next.title}</span>
        </a>
      )}
    </nav>
  );
}
